import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, MapPin } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { CONTACT } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you — your enquiry has reached JAKS Pharma.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[color:var(--relumia-ink)]/20 py-3 text-[15px] text-[color:var(--relumia-ink)] placeholder:text-[color:var(--relumia-muted)]/70 focus:outline-none focus:border-[color:var(--relumia-gold)] transition-colors";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative z-10 py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-2 gap-14 lg:gap-24">
        <div>
          <SectionLabel>Get Relumia</SectionLabel>
          <Reveal>
            <h2 className="font-serif font-light tracking-tight leading-[1.02] text-4xl md:text-6xl text-[color:var(--relumia-ink)]">
              Begin your <span className="italic text-gold">glow</span> journey.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-[15px] leading-relaxed text-[color:var(--relumia-muted)] max-w-md">
              Whether you&apos;re a customer, clinic or distributor — reach out and our
              team will get back to you. For orders and availability, drop us a line.
            </p>
          </Reveal>

          <div className="mt-12 space-y-5">
            {[
              [Mail, "Email", CONTACT.email, `mailto:${CONTACT.email}`],
              [Phone, "Phone", CONTACT.phone, `tel:${CONTACT.phone.replace(/\s/g, "")}`],
              [MessageCircle, "WhatsApp", CONTACT.whatsapp, CONTACT.whatsappUrl],
            ].map(([Icon, label, val, href]) => (
              <a
                key={label}
                href={href}
                target={href.startsWith("http") ? "_blank" : undefined}
                rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                data-testid={`contact-${label.toLowerCase()}`}
                className="flex items-center gap-4 group"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-champagne text-gold group-hover:bg-[color:var(--relumia-ink)] group-hover:text-white transition-colors">
                  <Icon size={18} strokeWidth={1.5} />
                </span>
                <span>
                  <span className="overline block">{label}</span>
                  <span className="text-[15px] text-[color:var(--relumia-ink)]">{val}</span>
                </span>
              </a>
            ))}

            <div className="flex items-start gap-4" data-testid="contact-address">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-champagne text-gold">
                <MapPin size={18} strokeWidth={1.5} />
              </span>
              <span>
                <span className="overline block">Corporate Office</span>
                <span className="text-[15px] leading-snug text-[color:var(--relumia-ink)] block max-w-xs">
                  {CONTACT.company}, {CONTACT.address}
                </span>
              </span>
            </div>
          </div>
        </div>

        <Reveal delay={0.05}>
          <form
            onSubmit={submit}
            data-testid="enquiry-form"
            className="bg-surface border border-champagne rounded-sm p-8 md:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <input
                data-testid="enquiry-name"
                className={inputCls}
                placeholder="Full name*"
                value={form.name}
                onChange={update("name")}
              />
              <input
                data-testid="enquiry-email"
                type="email"
                className={inputCls}
                placeholder="Email address*"
                value={form.email}
                onChange={update("email")}
              />
              <input
                data-testid="enquiry-phone"
                className={inputCls}
                placeholder="Phone"
                value={form.phone}
                onChange={update("phone")}
              />
              <input
                data-testid="enquiry-subject"
                className={inputCls}
                placeholder="Subject"
                value={form.subject}
                onChange={update("subject")}
              />
            </div>
            <textarea
              data-testid="enquiry-message"
              rows={4}
              className={`${inputCls} mt-6 resize-none`}
              placeholder="Your message*"
              value={form.message}
              onChange={update("message")}
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="enquiry-submit"
              className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-full bg-[color:var(--relumia-ink)] px-8 py-3.5 text-[13px] tracking-wide text-white hover:bg-[color:var(--relumia-gold-deep)] transition-colors disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send enquiry"}
              {!loading && <span>→</span>}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
